// src/pages/training/TrainingPage.tsx
import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Play,
  CheckCircle,
  Clock,
  Plus,
  Award,
  FileText,
  Video,
  HelpCircle,
  X,
  ChevronRight,
  Users,
  Calendar,
  Upload,
  Trash2,
  Settings
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { trainingService } from '../../services/training.service';
import {
  Course,
  CourseAssignment,
  CourseProgress,
  Certificate,
  CourseModule
} from '../../types/training.types';
import { format } from 'date-fns';
import { formatSafeDate } from '../../utils/dateUtils';
import { toast } from 'react-hot-toast';
import CreateCourseModal from '../../components/training/CreateCourseModal';

const TrainingPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [progress, setProgress] = useState<Record<string, CourseProgress>>({});
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'courses' | 'my-learning' | 'certificates'>('courses');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showCourseDetail, setShowCourseDetail] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';
  const isProviderRole = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  useEffect(() => {
    loadData();
  }, [currentUser, userProfile]);

  const loadData = async () => {
    if (!currentUser || !userProfile) return;

    try {
      // Provider users see all courses, others see their org's courses
      const coursePromise = isProviderRole
        ? trainingService.getAllCourses('published')
        : trainingService.getOrganizationCourses(userProfile.organizationId || '', 'published');

      const [courseList, assignmentList, certList] = await Promise.all([
        coursePromise,
        trainingService.getUserAssignments(currentUser.uid),
        trainingService.getUserCertificates(currentUser.uid)
      ]);

      setCourses(courseList);
      setAssignments(assignmentList);
      setCertificates(certList);

      // Load progress for each assignment
      const progressMap: Record<string, CourseProgress> = {};
      for (const assignment of assignmentList) {
        const prog = await trainingService.getCourseProgress(currentUser.uid, assignment.courseId);
        if (prog) {
          progressMap[assignment.courseId] = prog;
        }
      }
      setProgress(progressMap);
    } catch (error) {
      console.error('Error loading training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCourse = async (course: Course) => {
    if (!currentUser || !userProfile) return;

    try {
      await trainingService.startCourse(
        currentUser.uid,
        userProfile.displayName,
        course.id,
        course.title
      );
      setSelectedCourse(course);
      setShowCourseDetail(true);
      loadData();
    } catch (error) {
      console.error('Error starting course:', error);
      toast.error('Failed to start course');
    }
  };

  const getModuleIcon = (type: CourseModule['type']) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'quiz':
        return <HelpCircle className="h-4 w-4" />;
      default:
        return <BookOpen className="h-4 w-4" />;
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Training Center</h1>
          <p className="text-gray-500 mt-1">Learn new skills and track your progress</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
          >
            <Plus className="h-5 w-5" />
            Create Course
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
        {[
          { id: 'courses', label: 'All Courses', icon: BookOpen },
          { id: 'my-learning', label: 'My Learning', icon: Play },
          { id: 'certificates', label: 'Certificates', icon: Award }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* All Courses Tab */}
      {activeTab === 'courses' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No courses available</h3>
              <p className="text-gray-500">Check back later for new training content.</p>
            </div>
          ) : (
            courses.map((course) => {
              const courseProgress = progress[course.id];
              const isEnrolled = assignments.some(a => a.courseId === course.id);

              return (
                <div
                  key={course.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Thumbnail */}
                  <div className="h-40 bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-primary-400" />
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-800 line-clamp-1">{course.title}</h3>
                      {course.category && (
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                          {course.category}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{course.description}</p>

                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDuration(course.estimatedDuration)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {course.modules.length} modules
                      </span>
                    </div>

                    {/* Progress Bar */}
                    {courseProgress && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progress</span>
                          <span>{courseProgress.overallProgress}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all"
                            style={{ width: `${courseProgress.overallProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => handleStartCourse(course)}
                      className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                        isEnrolled
                          ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700'
                      }`}
                    >
                      {courseProgress?.status === 'completed'
                        ? 'Review Course'
                        : courseProgress
                          ? 'Continue Learning'
                          : 'Start Course'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* My Learning Tab */}
      {activeTab === 'my-learning' && (
        <div className="space-y-4">
          {assignments.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Play className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No assigned courses</h3>
              <p className="text-gray-500">You haven't started any courses yet.</p>
            </div>
          ) : (
            assignments.map((assignment) => {
              const course = courses.find(c => c.id === assignment.courseId);
              const courseProgress = progress[assignment.courseId];

              return (
                <div
                  key={assignment.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <BookOpen className="h-8 w-8 text-primary-500" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800">{assignment.courseName}</h3>
                        {assignment.required && (
                          <span className="px-2 py-0.5 bg-coral-100 text-coral-600 text-xs rounded-full">
                            Required
                          </span>
                        )}
                        {courseProgress?.status === 'completed' && (
                          <CheckCircle className="h-5 w-5 text-accent-500" />
                        )}
                      </div>

                      {/* Progress */}
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>
                            {courseProgress?.status === 'completed'
                              ? 'Completed'
                              : `${courseProgress?.overallProgress || 0}% complete`}
                          </span>
                          {assignment.dueDate && (
                            <span>
                              Due: {formatSafeDate(assignment.dueDate, 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              courseProgress?.status === 'completed'
                                ? 'bg-accent-500'
                                : 'bg-gradient-to-r from-primary-500 to-secondary-500'
                            }`}
                            style={{ width: `${courseProgress?.overallProgress || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => course && handleStartCourse(course)}
                      className="flex items-center gap-1 px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                    >
                      {courseProgress?.status === 'completed' ? 'Review' : 'Continue'}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Certificates Tab */}
      {activeTab === 'certificates' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No certificates yet</h3>
              <p className="text-gray-500">Complete courses to earn certificates.</p>
            </div>
          ) : (
            certificates.map((cert) => (
              <div
                key={cert.id}
                className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl border border-primary-100 p-6"
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <Award className="h-8 w-8 text-primary-500" />
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="font-semibold text-gray-800 mb-1">{cert.courseName}</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Issued {formatSafeDate(cert.issuedAt, 'MMM d, yyyy')}
                  </p>
                  <p className="text-xs text-gray-400 mb-4">
                    Verification: {cert.verificationCode}
                  </p>
                  <button className="w-full py-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors text-sm font-medium">
                    Download Certificate
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Course Detail Modal */}
      {showCourseDetail && selectedCourse && (
        <CourseDetailModal
          course={selectedCourse}
          progress={progress[selectedCourse.id]}
          onClose={() => {
            setShowCourseDetail(false);
            setSelectedCourse(null);
          }}
          onModuleComplete={() => loadData()}
        />
      )}

      {/* Create Course Modal */}
      {showCreateModal && (
        <CreateCourseModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadData();
          }}
          organizationId={userProfile?.organizationId || ''}
        />
      )}
    </div>
  );
};

// Course Detail Modal
interface CourseDetailModalProps {
  course: Course;
  progress?: CourseProgress;
  onClose: () => void;
  onModuleComplete: () => void;
}

const CourseDetailModal: React.FC<CourseDetailModalProps> = ({
  course,
  progress,
  onClose,
  onModuleComplete
}) => {
  const { currentUser, userProfile } = useAuth();
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [completing, setCompleting] = useState(false);

  const activeModule = course.modules[activeModuleIndex];
  const moduleProgress = progress?.moduleProgress.find(mp => mp.moduleId === activeModule?.id);

  const handleCompleteModule = async () => {
    if (!currentUser || !activeModule) return;

    setCompleting(true);
    try {
      await trainingService.updateModuleProgress(
        currentUser.uid,
        course.id,
        activeModule.id,
        {
          status: 'completed',
          completedAt: { toDate: () => new Date() } as any
        }
      );

      toast.success('Module completed!');
      onModuleComplete();

      // Move to next module if available
      if (activeModuleIndex < course.modules.length - 1) {
        setActiveModuleIndex(activeModuleIndex + 1);
      }
    } catch (error) {
      console.error('Error completing module:', error);
      toast.error('Failed to complete module');
    } finally {
      setCompleting(false);
    }
  };

  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5" />;
      case 'document': return <FileText className="h-5 w-5" />;
      case 'quiz': return <HelpCircle className="h-5 w-5" />;
      default: return <BookOpen className="h-5 w-5" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{course.title}</h2>
              <p className="text-sm text-gray-500 mt-1">
                Module {activeModuleIndex + 1} of {course.modules.length}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all"
                style={{ width: `${progress?.overallProgress || 0}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Module List */}
          <div className="w-64 border-r border-gray-100 overflow-y-auto">
            {course.modules.map((module, index) => {
              const modProgress = progress?.moduleProgress.find(mp => mp.moduleId === module.id);
              const isCompleted = modProgress?.status === 'completed';
              const isActive = index === activeModuleIndex;

              return (
                <button
                  key={module.id}
                  onClick={() => setActiveModuleIndex(index)}
                  className={`w-full p-4 text-left flex items-start gap-3 transition-colors ${
                    isActive
                      ? 'bg-primary-50 border-l-4 border-primary-500'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    isCompleted
                      ? 'bg-accent-100 text-accent-600'
                      : isActive
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-500'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      getModuleIcon(module.type)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      isActive ? 'text-primary-700' : 'text-gray-700'
                    }`}>
                      {module.title}
                    </p>
                    {module.duration && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {module.duration} min
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Content Area */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeModule && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-primary-100 rounded-xl">
                    {getModuleIcon(activeModule.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{activeModule.title}</h3>
                    <p className="text-sm text-gray-500 capitalize">{activeModule.type} module</p>
                  </div>
                </div>

                {activeModule.description && (
                  <p className="text-gray-600 mb-6">{activeModule.description}</p>
                )}

                {/* Module Content */}
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  {activeModule.type === 'video' && activeModule.videoUrl && (
                    <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                      <Play className="h-16 w-16 text-white opacity-50" />
                    </div>
                  )}

                  {activeModule.type === 'document' && activeModule.documentUrl && (
                    <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
                      <FileText className="h-10 w-10 text-primary-500" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {activeModule.documentName || 'Document'}
                        </p>
                        <p className="text-sm text-gray-500">PDF Document</p>
                      </div>
                      <a
                        href={activeModule.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                      >
                        View
                      </a>
                    </div>
                  )}

                  {activeModule.type === 'text' && activeModule.textContent && (
                    <div className="prose prose-sm max-w-none">
                      {activeModule.textContent}
                    </div>
                  )}

                  {activeModule.type === 'quiz' && activeModule.quiz && (
                    <div className="text-center py-8">
                      <HelpCircle className="h-12 w-12 text-primary-500 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        Quiz: {activeModule.quiz.questions.length} Questions
                      </h4>
                      <p className="text-gray-500 mb-4">
                        Passing score: {activeModule.quiz.settings.passingScore}%
                      </p>
                      <button className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                        Start Quiz
                      </button>
                    </div>
                  )}
                </div>

                {/* Complete Button */}
                {moduleProgress?.status !== 'completed' && (
                  <button
                    onClick={handleCompleteModule}
                    disabled={completing}
                    className="w-full py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-xl hover:from-accent-600 hover:to-accent-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {completing ? (
                      <>
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        Completing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        Mark as Complete
                      </>
                    )}
                  </button>
                )}

                {moduleProgress?.status === 'completed' && (
                  <div className="flex items-center justify-center gap-2 py-3 bg-accent-50 text-accent-700 rounded-xl">
                    <CheckCircle className="h-5 w-5" />
                    Module Completed
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingPage;
